import { useMemo } from 'react'
import {
  BarChart3, CalendarRange, Camera, Flag, Globe2, Mountain, Route as RouteIcon, Sparkles, TrendingUp, X,
} from 'lucide-react'

const COUNTRY_TARGET = 195
const STATUS_PRIORITY = { future: 0, planned: 1, completed: 2 }

function bestStatusByCountry(summits) {
  const byCountry = new Map()
  summits.forEach((summit) => {
    const key = String(summit.country_code || summit.country_name || '').trim().toUpperCase()
    if (!key) return
    const status = String(summit.status || 'future').toLowerCase()
    const previous = byCountry.get(key) || 'future'
    if ((STATUS_PRIORITY[status] || 0) >= (STATUS_PRIORITY[previous] || 0)) {
      byCountry.set(key, status)
    }
  })
  return byCountry
}

function formatMetres(value) {
  return `${Math.round(Number(value) || 0).toLocaleString()} m`
}

function formatKm(metres) {
  const km = (Number(metres) || 0) / 1000
  return `${km >= 100 ? Math.round(km).toLocaleString() : km.toFixed(1)} km`
}

export default function StatsDashboard({ open, onClose, trips, summits, routeLegs = [], photos = [] }) {
  const stats = useMemo(() => {
    const byCountry = bestStatusByCountry(summits)
    let completedCountries = 0
    let plannedCountries = 0
    byCountry.forEach((status) => {
      if (status === 'completed') completedCountries += 1
      if (status === 'planned') plannedCountries += 1
    })

    const completedSummits = summits.filter((summit) => summit.status === 'completed')
    const plannedSummits = summits.filter((summit) => summit.status === 'planned')
    const futureSummits = summits.filter((summit) => summit.status === 'future')

    const totalAscent = completedSummits.reduce((sum, summit) => sum + (Number(summit.elevation_m) || 0), 0)
    const highestCompleted = completedSummits.reduce(
      (best, summit) => (Number(summit.elevation_m) > Number(best?.elevation_m || -1) ? summit : best),
      null,
    )
    const highestAhead = [...plannedSummits, ...futureSummits].reduce(
      (best, summit) => (Number(summit.elevation_m) > Number(best?.elevation_m || -1) ? summit : best),
      null,
    )
    const averageCompleted = completedSummits.length ? totalAscent / completedSummits.length : 0
    const scenicChoices = summits.filter((summit) => summit.scenic_choice).length

    const byYear = new Map()
    completedSummits.forEach((summit) => {
      if (!summit.summit_date) return
      const year = new Date(`${summit.summit_date}T12:00:00`).getFullYear()
      if (!Number.isFinite(year)) return
      byYear.set(year, (byYear.get(year) || 0) + 1)
    })
    const years = [...byYear.entries()].sort((a, b) => a[0] - b[0])
    const maxYearCount = years.reduce((max, [, count]) => Math.max(max, count), 0)

    const completedTrips = trips.filter((trip) => trip.status === 'completed')
    const leaveDaysUsed = completedTrips.reduce((sum, trip) => sum + (Number(trip.leave_days) || 0), 0)
    const countriesFromCompletedTrips = completedTrips.reduce((sum, trip) => sum + (Number(trip.countries_count) || 0), 0)
    const bestEfficiencyTrip = trips
      .filter((trip) => Number(trip.leave_days) > 0 && Number(trip.countries_count) > 0)
      .map((trip) => ({ trip, ratio: Number(trip.countries_count) / Number(trip.leave_days) }))
      .sort((a, b) => b.ratio - a.ratio)[0] || null

    const mappedLegs = (routeLegs || []).filter((leg) => Number(leg.distance_m) > 0)
    const mappedDistance = mappedLegs.reduce((sum, leg) => sum + Number(leg.distance_m), 0)

    const photosWithGps = photos.filter(
      (photo) => Number.isFinite(Number(photo.latitude)) && Number.isFinite(Number(photo.longitude)),
    ).length

    return {
      completedCountries,
      plannedCountries,
      completedSummits,
      plannedSummits,
      futureSummits,
      totalAscent,
      highestCompleted,
      highestAhead,
      averageCompleted,
      scenicChoices,
      years,
      maxYearCount,
      completedTrips,
      leaveDaysUsed,
      countriesFromCompletedTrips,
      bestEfficiencyTrip,
      mappedLegs,
      mappedDistance,
      photosWithGps,
    }
  }, [trips, summits, routeLegs, photos])

  if (!open) return null

  const progressPercent = Math.min(100, (stats.completedCountries / COUNTRY_TARGET) * 100)

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose()
    }}>
      <section className="admin-panel stats-panel" role="dialog" aria-modal="true" aria-label="Summit Hub statistics">
        <header className="admin-panel__header">
          <div>
            <div className="eyebrow">Progress report</div>
            <h2>Statistics</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close statistics"><X size={19} /></button>
        </header>

        <div className="stats-body">
          <section className="stats-hero">
            <div className="stats-hero__head">
              <span className="stats-hero__icon"><Globe2 size={19} /></span>
              <div>
                <strong>{stats.completedCountries} of {COUNTRY_TARGET} countries summited</strong>
                <small>{stats.plannedCountries} more planned · {stats.completedSummits.length + stats.plannedSummits.length + stats.futureSummits.length} summit targets tracked</small>
              </div>
              <em>{progressPercent.toFixed(1)}%</em>
            </div>
            <div className="stats-progress-line" role="img" aria-label={`${stats.completedCountries} of ${COUNTRY_TARGET} countries completed`}>
              <span style={{ width: `${progressPercent}%` }} />
            </div>
          </section>

          <div className="stats-grid">
            <div className="stat-card">
              <Mountain size={16} />
              <strong>{formatMetres(stats.totalAscent)}</strong>
              <span>Total elevation of completed summits</span>
            </div>
            <div className="stat-card">
              <TrendingUp size={16} />
              <strong>{stats.highestCompleted ? formatMetres(stats.highestCompleted.elevation_m) : '—'}</strong>
              <span>{stats.highestCompleted ? `Highest summited · ${stats.highestCompleted.peak_name}` : 'Highest summited peak'}</span>
            </div>
            <div className="stat-card">
              <Flag size={16} />
              <strong>{stats.highestAhead ? formatMetres(stats.highestAhead.elevation_m) : '—'}</strong>
              <span>{stats.highestAhead ? `Biggest target ahead · ${stats.highestAhead.peak_name}` : 'Biggest target ahead'}</span>
            </div>
            <div className="stat-card">
              <BarChart3 size={16} />
              <strong>{stats.averageCompleted ? formatMetres(stats.averageCompleted) : '—'}</strong>
              <span>Average elevation per completed summit</span>
            </div>
            <div className="stat-card">
              <Sparkles size={16} />
              <strong>{stats.scenicChoices}</strong>
              <span>Iconic / scenic choices instead of the technical high point</span>
            </div>
            <div className="stat-card">
              <Camera size={16} />
              <strong>{photos.length}</strong>
              <span>{stats.photosWithGps} photos pinned to the map by GPS</span>
            </div>
          </div>

          <section className="stats-section">
            <div className="section-label">Summit status</div>
            <div className="stats-status-row">
              <span className="stats-status stats-status--completed"><i />{stats.completedSummits.length} completed</span>
              <span className="stats-status stats-status--planned"><i />{stats.plannedSummits.length} planned</span>
              <span className="stats-status stats-status--future"><i />{stats.futureSummits.length} future</span>
            </div>
          </section>

          {stats.years.length > 0 && (
            <section className="stats-section">
              <div className="section-label">Summits by year</div>
              <div className="stats-years">
                {stats.years.map(([year, count]) => (
                  <div className="stats-year-row" key={year}>
                    <span>{year}</span>
                    <div className="stats-year-bar">
                      <i style={{ width: `${(count / stats.maxYearCount) * 100}%` }} />
                    </div>
                    <em>{count}</em>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="stats-section">
            <div className="section-label">Trips &amp; leave efficiency</div>
            <div className="stats-grid stats-grid--tight">
              <div className="stat-card">
                <RouteIcon size={16} />
                <strong>{trips.length}</strong>
                <span>{stats.completedTrips.length} completed · {trips.filter((trip) => trip.status === 'active').length} active</span>
              </div>
              <div className="stat-card">
                <CalendarRange size={16} />
                <strong>{stats.leaveDaysUsed || '—'}</strong>
                <span>Annual-leave days spent on completed trips{stats.countriesFromCompletedTrips ? ` · ${stats.countriesFromCompletedTrips} countries` : ''}</span>
              </div>
              <div className="stat-card">
                <TrendingUp size={16} />
                <strong>{stats.bestEfficiencyTrip ? stats.bestEfficiencyTrip.ratio.toFixed(2) : '—'}</strong>
                <span>{stats.bestEfficiencyTrip ? `Best countries / leave day · ${stats.bestEfficiencyTrip.trip.name}` : 'Countries per leave day'}</span>
              </div>
              <div className="stat-card">
                <RouteIcon size={16} />
                <strong>{stats.mappedDistance ? formatKm(stats.mappedDistance) : '—'}</strong>
                <span>{stats.mappedLegs.length ? `Exact route distance across ${stats.mappedLegs.length} mapped legs` : 'Exact route distance (import Google legs to populate)'}</span>
              </div>
            </div>
          </section>
        </div>
      </section>
    </div>
  )
}
